/**
 * When a list may ask for its next page.
 *
 * Infinite scroll is one decision taken over and over: the sentinel at the
 * bottom of the list says "I am on screen", and the list answers with a
 * request. Getting that decision wrong does not show as a glitch — it shows as
 * a list that quietly pulls *every* page the moment it opens. A section with
 * 5 000 records then fires 200 requests nobody asked for, appends 5 000 rows to
 * the DOM, and the screen is unusable before the reader has scrolled once.
 *
 * The decision lives here, away from the DOM, so it can be tested without a
 * browser (`tests/e2e/unit/infinite-scroll.spec.js`).
 */

/**
 * Whether a box is the one its content actually scrolls inside.
 *
 * `overflow-y: auto` is not the question. A box that grows with its content —
 * a table wrapper with no height of its own — carries `auto` and clips
 * nothing: its scroll height and its client height are the same. Taking it for
 * the scroller is what breaks everything downstream, because the sentinel is
 * then *always* inside it, at any scroll position, and an IntersectionObserver
 * rooted there reports "visible" forever.
 */
export function scrollsItsContent(box) {
    if (!box || !/(auto|scroll|overlay)/.test(box.overflowY || '')) {
        return false;
    }

    return box.scrollHeight > box.clientHeight + 1;
}

/**
 * The nearest ancestor the sentinel really scrolls inside, or null when the
 * page itself is what scrolls.
 *
 * @param {Array} ancestors Boxes from the sentinel outwards
 */
export function resolveScrollRoot(ancestors = []) {
    return ancestors.find(scrollsItsContent) || null;
}

/**
 * The vertical band of the screen the reader is looking at the list through.
 *
 * It starts at the box that scrolls and is narrowed by every clipping ancestor
 * **above** it — a panel inside a frame inside the window. What is inside the
 * scroller is content, not a window, and must not count: the sentinel of a
 * table sits in a `<th>` that carries `overflow: hidden`, one line tall and
 * 25 000 px down the page. Folding that cell into the band collapsed it to
 * nothing, which made the band height zero, the read-ahead distance fall back
 * to its floor, and the next page get asked for at the very last pixel — the
 * stutter this whole file exists to avoid.
 *
 * This stays deliberately independent of the observer's root: it is the second
 * opinion that keeps a mis-resolved root from turning into a burst of requests,
 * and it is what makes "the reader reached the bottom" mean the same thing in a
 * page, in a panel and in a kanban column.
 */
export function visibleBand(ancestors = [], viewportHeight = 0) {
    const rootIndex = ancestors.findIndex(scrollsItsContent);
    // Nothing scrolls yet: the list fits its box, so every ancestor is a window.
    const windows = rootIndex === -1 ? ancestors : ancestors.slice(rootIndex);

    let top = 0;
    let bottom = viewportHeight;

    for (const box of windows) {
        if ((box.overflowY || 'visible') === 'visible') {
            continue;
        }

        top = Math.max(top, box.top);
        bottom = Math.min(bottom, box.bottom);
    }

    return { top, bottom };
}

/**
 * How far ahead of the reader the next page is fetched.
 *
 * Measured in screens, not pixels: a hundred pixels of warning is no warning at
 * all — the request leaves as the reader arrives, and every page is a visible
 * stop. Keeping a couple of screens of rows already loaded below the fold is
 * what makes a long list feel like it has no pages at
 * all, because the reader never catches up with the loading.
 *
 * @param {Object} options
 * @param {number} options.bandHeight Height of the visible part of the list
 * @param {number} options.screens How many of those to stay ahead by
 * @param {number} options.minimum Floor, for a list rendered in a tiny box
 */
export function prefetchDistance({ bandHeight = 0, screens = 2, minimum = 100 }) {
    return Math.max(minimum, Math.round(bandHeight * screens));
}

/**
 * Has the reader actually come within `distance` of the sentinel?
 *
 * @param {Object} options
 * @param {{top: number, bottom: number}} options.sentinel Sentinel rectangle
 * @param {Array} options.ancestors Clipping ancestors, sentinel outwards
 * @param {number} options.viewportHeight
 * @param {number} options.distance How early the next page may be asked for
 */
export function isSentinelReached({ sentinel, ancestors = [], viewportHeight = 0, distance = 0 }) {
    if (!sentinel) {
        return false;
    }

    const { top, bottom } = visibleBand(ancestors, viewportHeight);

    // A band with no height means the sentinel's panel is itself off screen or
    // collapsed: nobody is looking at this list.
    if (bottom <= top) {
        return false;
    }

    return sentinel.top <= bottom + distance && sentinel.bottom >= top - distance;
}

/**
 * What to do with an intersection report.
 *
 * `blocked` is the budget guard: a handful of pages may be pulled in a row to
 * fill a screen that a single page leaves short, but past that the list waits
 * for the reader to scroll again. Without it, any future layout change that
 * fools the geometry check is a runaway loop again rather than a few extra
 * requests.
 *
 * The budget only applies to a list that has something to scroll. A list still
 * shorter than its own box has no scroll to wait for, so capping it there would
 * strand it: the rows it is missing are the ones that would let the reader ask
 * for more.
 *
 * It is set well above what filling the prefetch buffer takes, so it never cuts
 * a legitimate run of pages short — the buffer, not the budget, is what ends a
 * normal burst.
 *
 * @returns {'load'|'blocked'|'idle'}
 */
export function decideLoad({ state, reached, autoLoads = 0, maxAutoLoads = 8, canScroll = true }) {
    if (state !== 'ready' || !reached) {
        return 'idle';
    }

    if (canScroll && autoLoads >= maxAutoLoads) {
        return 'blocked';
    }

    return 'load';
}
